<script setup>
import { computed } from 'vue'
import { formatarDataBR } from '@/utils/useDataBR'

const props = defineProps({
  itemDetalhes: { type: Object, required: true },
  listas: { type: Object, required: true },
  formatarValor: { type: Function, default: (v) => v },
  getTextoLista: { type: Function, default: (l, id) => id || '-' },
  formatarMoedaComSigla: { type: Function, default: (v, m) => `${v} ${m}` }
})

// Indica se o modo de precificação é "Por Tabela" (PED_VEN_MODO_PRECIFIC1)
const ehPrecificacaoPorTabela = computed(() => {
  return props.itemDetalhes.lstModoPrecificacao === 'PED_VEN_MODO_PRECIFIC1'
})

// Há margem esperada a exibir?
const temMargemEsperada = computed(() => {
  return props.itemDetalhes.margemEsperadaPercentual != null
})

// Há margem efetiva global calculada a partir do custo?
const temMargemEfetiva = computed(() => {
  return props.itemDetalhes.margemEfetivaGlobalPercentual != null
})

// Análise cruzada com a tolerância (visão global por pedido)
const analiseTolerancia = computed(() => {
  const esperada = props.itemDetalhes.margemEsperadaPercentual
  const efetiva = props.itemDetalhes.margemEfetivaGlobalPercentual
  const tolerancia = props.itemDetalhes.percentualTolerancia

  if (efetiva == null) {
    return {
      classe: 'badge-ghost-secondary',
      fundo: 'bg-secondary-subtle',
      titulo: 'Sem margem efetiva',
      texto: 'Não há custo disponível para calcular a margem efetiva.'
    }
  }

  if (esperada == null) {
    return {
      classe: temMargemEfetiva.value ? 'badge-ghost-info' : 'badge-ghost-secondary',
      fundo: 'bg-info-subtle',
      titulo: `Margem efetiva: ${props.formatarValor(efetiva)}%`,
      texto: 'Pedido sem margem esperada de referência (precificação de projeto).'
    }
  }

  const delta = efetiva - esperada
  if (delta >= 0) {
    return {
      classe: 'badge-ghost-success',
      fundo: 'bg-success-subtle',
      titulo: `Margem efetiva ${props.formatarValor(efetiva)}% — acima da esperada`,
      texto: `${props.formatarValor(Math.abs(delta))} p.p. acima da margem esperada (${props.formatarValor(esperada)}%).`
    }
  }

  if (tolerancia != null && Math.abs(delta) <= tolerancia) {
    return {
      classe: 'badge-ghost-warning',
      fundo: 'bg-warning-subtle',
      titulo: `Margem efetiva ${props.formatarValor(efetiva)}% — dentro da tolerância`,
      texto: `${props.formatarValor(Math.abs(delta))} p.p. abaixo da esperada (${props.formatarValor(esperada)}%), dentro da tolerância de ${props.formatarValor(tolerancia)}%.`
    }
  }

  return {
    classe: 'badge-ghost-danger',
    fundo: 'bg-danger-subtle',
    titulo: `Margem efetiva ${props.formatarValor(efetiva)}% — FORA da tolerância`,
    texto: `${props.formatarValor(Math.abs(delta))} p.p. abaixo da esperada (${props.formatarValor(esperada)}%), acima da tolerância de ${props.formatarValor(tolerancia ?? 0)}%.`
  }
})
</script>

<template>
  <!-- ⚠️ Alerta: margem real calculada -->
  <div v-if="itemDetalhes.margemReal" class="alert alert-info border-0 py-2 px-3 small mb-3 d-flex align-items-center gap-2">
    <i class="bi bi-info-circle-fill"></i>
    <span>Margem abaixo do esperado <strong>aceita</strong> na negociação.</span>
  </div>

  <!-- 1️⃣ Dados de identificação do pedido (cards) -->
  <div class="row g-2 mb-3">
    <div class="col-6 col-md-3">
      <div class="border rounded p-2 h-100 bg-body-tertiary">
        <small class="text-body-secondary d-block lh-1 mb-1">Nº Pedido</small>
        <span class="fw-medium text-body-emphasis small">{{ itemDetalhes.numeroPedidoVenda || '-' }}</span>
      </div>
    </div>
    <div class="col-6 col-md-3">
      <div class="border rounded p-2 h-100 bg-body-tertiary">
        <small class="text-body-secondary d-block lh-1 mb-1">Cliente</small>
        <span class="fw-medium text-body-emphasis small">{{ itemDetalhes.nomeCliente || '-' }}</span>
      </div>
    </div>
    <div class="col-6 col-md-3">
      <div class="border rounded p-2 h-100 bg-body-tertiary">
        <small class="text-body-secondary d-block lh-1 mb-1">Moeda</small>
        <span class="fw-medium text-body-emphasis small">{{ getTextoLista(listas.LstMoeda, itemDetalhes.lstMoeda) }}</span>
      </div>
    </div>
    <div class="col-6 col-md-3">
      <div class="border rounded p-2 h-100 bg-body-tertiary">
        <small class="text-body-secondary d-block lh-1 mb-1">Status do Pedido</small>
        <span class="badge badge-ghost-info">{{ itemDetalhes.nomeStatusWorkflowPedido || getTextoLista(listas.LstStatusWorkflowPedido, itemDetalhes.lstStatusWorkflowPedido) }}</span>
      </div>
    </div>
    <div class="col-6 col-md-3">
      <div class="border rounded p-2 h-100 bg-body-tertiary">
        <small class="text-body-secondary d-block lh-1 mb-1">Status Análise</small>
        <span class="badge" :class="itemDetalhes.lstStatusAprovacao === 'WORK_APR_ST3' ? 'badge-ghost-success' : itemDetalhes.lstStatusAprovacao === 'WORK_APR_ST4' ? 'badge-ghost-danger' : itemDetalhes.lstStatusAprovacao === 'WORK_APR_ST2' ? 'badge-ghost-warning' : 'badge-ghost-secondary'">
          {{ itemDetalhes.nomeStatus || getTextoLista(listas.LstStatusAprovacao, itemDetalhes.lstStatusAprovacao) || '-' }}
        </span>
      </div>
    </div>
    <div class="col-6 col-md-3">
      <div class="border rounded p-2 h-100 bg-body-tertiary">
        <small class="text-body-secondary d-block lh-1 mb-1">Grupo Aprovador</small>
        <span class="fw-medium text-body-emphasis small">{{ itemDetalhes.nomeGrupoAprovador || '-' }}</span>
      </div>
    </div>
    <div class="col-6 col-md-3">
      <div class="border rounded p-2 h-100 bg-body-tertiary">
        <small class="text-body-secondary d-block lh-1 mb-1">Aprovador</small>
        <span class="fw-medium text-body-emphasis small">{{ itemDetalhes.nomeAprovador || 'Qualquer aprovador do grupo' }}</span>
      </div>
    </div>
    <div class="col-6 col-md-3">
      <div class="border rounded p-2 h-100 bg-body-tertiary">
        <small class="text-body-secondary d-block lh-1 mb-1">Enviado p/ Aprovação</small>
        <span class="fw-medium text-body-emphasis small">{{ itemDetalhes.tsEnviadoAprovacao ? formatarDataBR(itemDetalhes.tsEnviadoAprovacao) : '-' }}</span>
      </div>
    </div>
  </div>

  <!-- 2️⃣ Breakdown de valores + análise de margem global -->
  <div class="bg-body-tertiary border rounded p-2 mb-3">
    <small class="fw-bold text-body-emphasis d-block mb-2">
      <i class="bi bi-calculator me-1 text-info"></i> Breakdown de Valores
    </small>

    <div class="row g-1 small">
      <div class="col-6 col-md-3">
        <span class="text-body-secondary">Original:</span>
        <span class="fw-medium ms-1">{{ formatarValor(itemDetalhes.valorTotalOriginal) }}</span>
      </div>
      <div class="col-6 col-md-3">
        <span class="text-body-secondary">Desconto:</span>
        <span class="fw-medium ms-1">{{ formatarValor(itemDetalhes.valorTotalDesconto) }}</span>
      </div>
      <div class="col-6 col-md-3">
        <span class="text-body-secondary">Frete:</span>
        <span class="fw-medium ms-1">{{ formatarValor(itemDetalhes.valorTotalFrete) }}</span>
      </div>
      <div class="col-6 col-md-3">
        <span class="text-body-secondary fw-bold">Negociado:</span>
        <span class="fw-bold ms-1">{{ formatarValor(itemDetalhes.valorTotalNegociado) }}</span>
      </div>
    </div>

    <div class="mt-2 pt-2 border-top border-secondary-subtle d-flex flex-wrap align-items-center gap-2 small">
      <template v-if="ehPrecificacaoPorTabela && temMargemEsperada">
        <span class="text-body-secondary">Esperada:</span>
        <span class="badge badge-ghost-info" style="font-size: 0.85rem;">
          {{ formatarValor(itemDetalhes.margemEsperadaPercentual) }}%
        </span>
      </template>
      <template v-if="temMargemEfetiva">
        <span class="text-body-secondary">Efetiva:</span>
        <span class="badge" :class="analiseTolerancia.classe" style="font-size: 0.85rem;">
          {{ formatarValor(itemDetalhes.margemEfetivaGlobalPercentual) }}%
        </span>
      </template>
      <span v-if="itemDetalhes.percentualTolerancia != null" class="text-body-secondary">
        Tolerância:
        <span class="fw-medium text-body-emphasis">{{ formatarValor(itemDetalhes.percentualTolerancia) }}%</span>
      </span>
      <span v-if="temMargemEsperada || temMargemEfetiva" class="text-body-secondary ms-auto">
        Precificação:
        <span class="fw-medium text-body-emphasis">{{ itemDetalhes.nomeModoPrecificacao || '-' }}</span>
      </span>
      <span v-if="itemDetalhes.nomeTabelaPreco" class="text-body-secondary">
        Tabela:
        <span class="fw-medium text-body-emphasis">{{ itemDetalhes.nomeTabelaPreco }}</span>
      </span>
      <span v-if="itemDetalhes.operacaoFinanciada" class="text-body-secondary">
        <span class="badge badge-ghost-warning" style="font-size: 0.85rem;">
          <i class="bi bi-currency-exchange me-1"></i>Operação Financiada
        </span>
      </span>
    </div>

    <!-- Análise cruzada com a tolerância (global por pedido) — título e texto lado a lado -->
    <div v-if="temMargemEsperada || temMargemEfetiva" class="d-flex align-items-start gap-2 mt-2 p-2 border rounded" :class="analiseTolerancia.fundo">
      <i class="bi bi-graph-up-arrow mt-1"></i>
      <div class="small lh-sm">
        <span class="fw-bold text-body-emphasis">{{ analiseTolerancia.titulo }}.</span>
        <span class="text-body-secondary"> {{ analiseTolerancia.texto }}</span>
      </div>
    </div>
  </div>

  <!-- 3️⃣ Resumo em destaque (2 colunas: solicitante | texto) -->
  <div class="bg-body-tertiary border rounded p-2 mb-3">
    <div class="row g-2 small">
      <div class="col-md-4 col-lg-3">
        <div class="d-flex align-items-start gap-2" v-if="itemDetalhes.nomeSolicitante">
          <i class="bi bi-person-fill text-info"></i>
          <div>
            <small class="text-body-secondary d-block lh-1">Solicitante</small>
            <span class="fw-medium text-body-emphasis">{{ itemDetalhes.nomeSolicitante }}</span>
          </div>
        </div>
        <div v-else class="text-body-secondary small">—</div>
      </div>
      <div class="col-md-8 col-lg-9">
        <div class="d-flex align-items-start gap-2">
          <i class="bi bi-chat-quote text-info"></i>
          <div>
            <small class="text-body-secondary d-block lh-1">Resumo</small>
            <span class="text-body-secondary">{{ itemDetalhes.resumoSolicitacao || 'Nenhum resumo registrado.' }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Componente enxuto: sem grids de produtos/condições — apenas o cabeçalho analítico */
</style>
